import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation as useRouterLocation } from "react-router-dom";
import apiClient from "@/lib/api-client";
import { useAuth } from "./AuthContext";
import { useLocation } from "./LocationContext";
import { SETUP_TASKS_QUERY_KEY } from "@/hooks/useSetupTasks";
import SetupWizardModal from "@/components/setup-wizard/SetupWizardModal";

/**
 * ASISTENTE GUIADO DE CONFIGURACIÓN (27-sep-2026, petición de Luis).
 *
 * Por qué existe, con el caso real delante: los negocios de CITAS se registran,
 * crean un servicio y se quedan ahí. Sin profesional con horario el widget no
 * ofrece ni una hora, así que no reciben ni una cita y se van pensando que la
 * herramienta no va. La guía que había (`/appointments/guide`) y los avisos del
 * asistente de puesta en marcha solo ENLAZAN a la pantalla; este asistente le
 * hace rellenar los datos ahí mismo, paso a paso, sin salir de la ventana.
 *
 * Reparto de responsabilidades:
 *   - El backend (`GET /setup-wizard`) dice QUÉ FALTA, del dato real.
 *   - Los datos se crean con los endpoints de siempre (`/services`,
 *     `/specialists`, `/appointment-settings`…): el asistente no tiene una vía
 *     paralela que pudiera quedarse atrás cuando esos formularios cambien.
 *
 * Este contexto es solo el mando: estado, apertura y cierre. La ventana vive en
 * `components/setup-wizard/`.
 */

export type WizardPasoId =
  | "horario"
  | "servicios"
  | "profesionales"
  | "salas"
  | "mesas"
  | "enlace";

/** Ver `setup-wizard/service.ts`: "opcional" no cuenta como pendiente. */
export type WizardNivel = "imprescindible" | "recomendado" | "opcional";

export interface WizardPaso {
  id: WizardPasoId;
  nivel: WizardNivel;
  obligatorio: boolean;
  hecho: boolean;
}

export interface WizardDatosCitas {
  horarioGuardado: boolean;
  diasAbiertos: number;
  servicios: number;
  profesionales: number;
  profesionalesListos: number;
  profesionalesSinHorario: number;
  profesionalesSinServicios: number;
  /** Los que están a medias, con nombre y qué les falta (tope de 12). */
  profesionalesIncompletos: {
    id: string;
    nombre: string;
    faltaHorario: boolean;
    faltanServicios: boolean;
  }[];
  salas: number;
}

export interface WizardDatosMesas {
  diasAbiertos: number;
  turnosActivos: number;
  salas: number;
  mesas: number;
}

export interface SetupWizardEstado {
  mostrar: boolean;
  bookingMode: "tables" | "appointments";
  sede: { id: string; nombre: string; slug: string } | null;
  enlaceReservas: string | null;
  pasos: WizardPaso[];
  pendientesObligatorios: number;
  pendientes: number;
  puedeRecibir: boolean;
  autoAbrir: boolean;
  recibeReservas: boolean;
  tieneAlgunaReserva: boolean;
  citas: WizardDatosCitas | null;
  mesas: WizardDatosMesas | null;
  cerrado: boolean;
  aplazadoHasta: string | null;
}

export const SETUP_WIZARD_QUERY_KEY = ["organization", "setup-wizard"] as const;

/** Ya se ha ofrecido solo en esta pestaña: no vuelve a saltar al navegar. */
const OFRECIDO_KEY = "rm_setup_wizard_ofrecido";
/** Margen tras cargar el panel, para no saltar encima de la pantalla al entrar. */
const RETARDO_MS = 2500;

interface SetupWizardValue {
  estado: SetupWizardEstado | null;
  abierto: boolean;
  /** Abre el asistente (botón del panel, tarjeta, guía…). */
  abrir: (paso?: WizardPasoId) => void;
  /** Cierra la ventana. `paraSiempre` = no volver a abrirla sola. */
  cerrar: (paraSiempre?: boolean) => void;
  /** Vuelve a preguntar al backend qué falta (tras crear algo). */
  refrescar: () => void;
  /** Paso por el que se abre (el primero sin hacer, o el que se pidió). */
  pasoInicial: WizardPasoId | null;
  /**
   * Queda algo por configurar y el asistente puede ofrecerlo. Lo usan la
   * tarjeta del panel y el popup de puesta en marcha (que se calla mientras
   * esto sea `true`: un aviso a la vez).
   */
  pendiente: boolean;
}

const SetupWizardContext = createContext<SetupWizardValue | null>(null);

function unwrap(raw: unknown): SetupWizardEstado | null {
  const data =
    raw && typeof raw === "object" && "data" in raw
      ? (raw as { data?: unknown }).data
      : raw;
  if (!data || typeof data !== "object") return null;
  const estado = data as SetupWizardEstado;
  return estado.mostrar ? estado : null;
}

export function SetupWizardProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { currentLocationId } = useLocation();
  const routerLocation = useRouterLocation();
  const queryClient = useQueryClient();
  const esAdmin = user?.role === "admin" || user?.role === "owner";

  const [abierto, setAbierto] = useState(false);
  const [pasoInicial, setPasoInicial] = useState<WizardPasoId | null>(null);

  const { data: estado } = useQuery({
    // La sede entra en la clave: el asistente se calcula por sede (los
    // profesionales y el enlace del widget son de una sede concreta).
    queryKey: [...SETUP_WIZARD_QUERY_KEY, currentLocationId],
    queryFn: async () => {
      const res = await apiClient.get("/setup-wizard");
      return unwrap(res.data);
    },
    enabled: esAdmin,
    // Silencioso: si falla, el asistente simplemente no se ofrece.
    retry: false,
  });

  const cerrarMutation = useMutation({
    mutationFn: async (paraSiempre: boolean) => {
      await apiClient.post("/setup-wizard/cerrar", { paraSiempre });
    },
    onError: () => {},
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETUP_WIZARD_QUERY_KEY });
    },
  });

  const refrescar = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: SETUP_WIZARD_QUERY_KEY });
    // El resumen del panel mira los mismos datos: si aquí se crea un servicio,
    // allí tiene que desaparecer el aviso.
    queryClient.invalidateQueries({ queryKey: SETUP_TASKS_QUERY_KEY });
  }, [queryClient]);

  const abrir = useCallback((paso?: WizardPasoId) => {
    setPasoInicial(paso ?? null);
    setAbierto(true);
  }, []);

  const cerrar = useCallback(
    (paraSiempre = false) => {
      setAbierto(false);
      setPasoInicial(null);
      // Se marca como ofrecido también al cerrar a mano: en esta pestaña no
      // vuelve a saltar solo aunque se navegue.
      try {
        sessionStorage.setItem(OFRECIDO_KEY, "1");
      } catch {
        // Modo privado: como mucho se vuelve a ofrecer al navegar.
      }
      cerrarMutation.mutate(paraSiempre);
    },
    [cerrarMutation],
  );

  /**
   * Apertura automática. Tres condiciones, todas necesarias:
   *   1. El backend dice que toca (`autoAbrir`: le falta algo, no ha recibido
   *      ninguna reserva por el widget y no lo ha cerrado ni aplazado).
   *   2. Está en el PANEL. Saltar una ventana encima mientras el cliente está
   *      atendiendo una reserva sería un estorbo; el panel es donde aterriza al
   *      entrar.
   *   3. No se ha ofrecido ya en esta pestaña.
   */
  useEffect(() => {
    if (!estado?.autoAbrir || routerLocation.pathname !== "/") return;
    let ofrecido = false;
    try {
      ofrecido = sessionStorage.getItem(OFRECIDO_KEY) === "1";
    } catch {
      ofrecido = false;
    }
    if (ofrecido) return;

    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(OFRECIDO_KEY, "1");
      } catch {
        // ignorado a propósito
      }
      setAbierto(true);
    }, RETARDO_MS);
    return () => clearTimeout(timer);
  }, [estado?.autoAbrir, routerLocation.pathname]);

  const value = useMemo<SetupWizardValue>(
    () => ({
      estado: estado ?? null,
      abierto,
      abrir,
      cerrar,
      refrescar,
      pasoInicial,
      // Si ha dicho "no volver a enseñarlo", esto se apaga: la tarjeta del
      // panel desaparece y el popup de puesta en marcha vuelve a hablar (el
      // asistente sigue accesible desde la guía).
      pendiente: !!estado && estado.pendientes > 0 && !estado.cerrado,
    }),
    [estado, abierto, abrir, cerrar, refrescar, pasoInicial],
  );

  return (
    <SetupWizardContext.Provider value={value}>
      {children}
      {abierto && estado && <SetupWizardModal />}
    </SetupWizardContext.Provider>
  );
}

/**
 * Mando del asistente. Fuera del provider devuelve un mando apagado en vez de
 * lanzar: así un componente puede pedirlo sin condicionar dónde se monta.
 */
export function useSetupWizard(): SetupWizardValue {
  const ctx = useContext(SetupWizardContext);
  if (ctx) return ctx;
  return {
    estado: null,
    abierto: false,
    abrir: () => {},
    cerrar: () => {},
    refrescar: () => {},
    pasoInicial: null,
    pendiente: false,
  };
}
